import { type CSSProperties, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '@helios/ui';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { qHomeQueueDetail, type HomeQueueDetailRow } from '@helios/data-access';
import {
  DrawerSummaryBanner,
  DrawerRowCard,
  DrawerFooterButton,
  DrawerEmptyState,
  SectionLabel,
} from './shared';

export interface InQueueDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  today: string;
  portalStyle?: CSSProperties;
}

export function InQueueDrawer({ open, onOpenChange, today, portalStyle }: InQueueDrawerProps) {
  const navigate = useNavigate();
  const { data: rows = [] } = usePowerSyncQuery<HomeQueueDetailRow>(qHomeQueueDetail(today));

  const { serving, called, waiting } = useMemo(() => {
    return {
      serving: rows.filter((r) => r.status === 'serving'),
      called: rows.filter((r) => r.status === 'called'),
      waiting: rows.filter((r) => r.status === 'waiting'),
    };
  }, [rows]);

  const queueNumStyle = (bg: string): CSSProperties => ({
    minWidth: 48,
    height: 28,
    padding: '0 8px',
    borderRadius: 'var(--radius-md, 10px)',
    background: bg,
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  });

  const renderEntry = (entry: HomeQueueDetailRow, numColor: string, borderColor: string) => (
    <DrawerRowCard key={entry.id} borderColor={borderColor}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
        <div style={queueNumStyle(numColor)}>
          Q-{String(entry.queue_number ?? 0).padStart(3, '0')}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--font-size-sm, 13px)',
              fontWeight: 'var(--font-weight-semi, 600)',
              color: 'var(--text, #111827)',
            }}
          >
            {entry.patient_name ?? 'Unknown'}
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--text-muted, #6b7280)',
            }}
          >
            {entry.service_name ?? '—'}
          </div>
        </div>
      </div>
      {entry.provider_name && (
        <div
          style={{
            fontSize: 'var(--font-size-xs, 12px)',
            color: 'var(--text-muted, #6b7280)',
          }}
        >
          Provider: {entry.provider_name}
        </div>
      )}
    </DrawerRowCard>
  );

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="In Queue Now"
      description="Real-time queue status"
      width={520}
      portalStyle={portalStyle}
      footer={
        <DrawerFooterButton
          label="Open Queue"
          color="#d97706"
          onClick={() => {
            navigate('/clinic/queue');
            onOpenChange(false);
          }}
        />
      }
    >
      <DrawerSummaryBanner
        icon="list-ordered"
        iconBg="#fefce8"
        iconColor="#ca8a04"
        value={String(rows.length)}
        description="Total patients in queue"
        breakdown={
          <span>
            <span style={{ color: '#6b7280' }}>• Waiting: </span>
            <strong>{waiting.length}</strong>
            <span style={{ color: '#d97706' }}> &nbsp;• Calling: </span>
            <strong>{called.length}</strong>
            <span style={{ color: '#16a34a' }}> &nbsp;• Serving: </span>
            <strong>{serving.length}</strong>
          </span>
        }
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3, 12px)',
          padding: 'var(--sp-4, 16px)',
        }}
      >
        {rows.length === 0 && <DrawerEmptyState message="No patients in queue" />}

        {serving.length > 0 && (
          <>
            <SectionLabel dot="#16a34a" label="NOW SERVING" />
            {serving.map((e) => renderEntry(e, '#16a34a', '#bbf7d0'))}
          </>
        )}

        {called.length > 0 && (
          <>
            <SectionLabel dot="#d97706" label="NOW CALLING" />
            {called.map((e) => renderEntry(e, '#f59e0b', '#fde68a'))}
          </>
        )}

        {waiting.length > 0 && (
          <>
            <SectionLabel dot="#6b7280" label="WAITING" />
            {waiting.map((e) => renderEntry(e, '#6b7280', 'var(--border, #e5e7eb)'))}
          </>
        )}
      </div>
    </Drawer>
  );
}
